import { useEffect, useRef, useState } from 'react';

import type { DatoContext } from '../stores-v2/types';
import type { SessionResource, UserResource } from '@clerk/types';

declare global {
  interface Window {
    __ARBOR_MCP_AUTH?: {
      token: string;
      userId: string;
      email: string;
      orgId: string;
      orgName: string;
    };
    pylon?: {
      chat_settings: {
        app_id: string;
        email: string | null;
        email_hash?: string | null;
        name: string | null;
      };
    };
  }
}

const DEV_CLERK_TIMEOUT_MS = 3000;

/**
 * Grace window for an already-authenticated user whose Clerk session
 * momentarily reads null. Clerk re-syncs/refreshes its session on window focus
 * & visibilitychange (e.g. tearing a browser tab out into a new window), and
 * can briefly report `{ isLoaded: true, session: null }` mid-refresh.
 * Redirecting on that transient blip bounced signed-in users to /sign-in —
 * sometimes twice, via the redirect → cookie-still-valid → bounce-back loop
 * (PLAT-11031). Hold the redirect this long; if the session returns first we
 * cancel it. Long enough to cover a slow refresh, short enough that a genuine
 * sign-out still feels immediate.
 */
export const REDIRECT_GRACE_MS = 750;

interface AuthInitParams {
  datoCtx?: DatoContext;
  clerkUser?: UserResource | null;
  clerkSession?: SessionResource | null;
  isClerkLoaded?: boolean;
  /**
   * True while an SSO ticket sign-in (useTicketSignIn) is in flight. Clerk
   * reports "loaded, no session" during that window, which must not be
   * mistaken for "signed out" — a stale needsRedirect=true set then would
   * mount RedirectToSignIn the moment the ticket completes (child effects
   * run before this hook's effect can clear the flag) and Clerk would
   * bounce the freshly signed-in user to its configured Home URL.
   */
  ticketSignInPending?: boolean;
}

export function useAuthInitialization({
  datoCtx,
  clerkUser,
  clerkSession,
  isClerkLoaded,
  ticketSignInPending,
}: AuthInitParams) {
  const [authState, setAuthState] = useState<{
    isAuthReady: boolean;
    needsRedirect: boolean;
    hasValidAuth: boolean;
  }>({
    isAuthReady: false,
    needsRedirect: false,
    hasValidAuth: false,
  });
  const [clerkTimedOut, setClerkTimedOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Redirect grace (PLAT-11031): whether the user has ever been authenticated
  // this session, the pending grace timer, and whether that grace window has
  // elapsed (flipping this lets the effect re-run and finalize the redirect).
  const wasAuthenticatedRef = useRef(false);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [graceExpired, setGraceExpired] = useState(false);

  // Clear any pending redirect-grace timer on unmount.
  useEffect(
    () => () => {
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
    },
    [],
  );

  // Dev-mode timeout: if Clerk hasn't loaded in 3s, proceed without auth
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (isClerkLoaded || clerkTimedOut) return;

    timerRef.current = setTimeout(() => {
      console.warn(
        'Clerk failed to load within timeout — proceeding in dev mode without auth',
      );
      setClerkTimedOut(true);
    }, DEV_CLERK_TIMEOUT_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isClerkLoaded, clerkTimedOut]);

  useEffect(() => {
    // MCP headless mode: skip Clerk entirely when auth is injected
    if (window.__ARBOR_MCP_AUTH?.token) {
      setAuthState({
        isAuthReady: true,
        needsRedirect: false,
        hasValidAuth: true,
      });
      return;
    }

    // Dev mode: Clerk timed out, permanently bypass auth
    if (clerkTimedOut) {
      setAuthState({
        isAuthReady: true,
        needsRedirect: false,
        hasValidAuth: true,
      });
      return;
    }

    // Wait for Clerk to finish loading if we don't have Dato auth
    if (!datoCtx?.currentUserAccessToken && !isClerkLoaded) {
      return;
    }

    const hasClerkAuth = !!clerkSession;
    const hasDatoAuth = !!datoCtx?.currentUserAccessToken;

    if (process.env.REACT_APP_PYLON_APP_ID && hasClerkAuth) {
      const userEmail = clerkUser?.emailAddresses?.[0]?.emailAddress;

      window.pylon = {
        chat_settings: {
          app_id: process.env.REACT_APP_PYLON_APP_ID,
          email: userEmail || null,
          name: clerkUser?.fullName || null,
        },
      };

      const pylonDomain = process.env.REACT_APP_PYLON_VERIFICATION_DOMAIN;
      if (userEmail && pylonDomain) {
        fetch(`https://${pylonDomain}/pylon/verification`, {
          headers: {
            'X-User-Email': userEmail,
          },
        })
          .then(response => response.json())
          .then((data: { verificationHash: string }) => {
            if (window.pylon && data.verificationHash) {
              window.pylon.chat_settings.email_hash = data.verificationHash;
            }
          })
          .catch(error => {
            console.error('Failed to fetch Pylon email hash:', error);
          });
      }
    }

    // Add more detailed debugging
    console.debug('Detailed Auth Status:', {
      hasClerkAuth,
      hasDatoAuth,
      isClerkLoaded,
      clerkSessionStatus: clerkSession?.status,
      clerkUserId: clerkUser?.id,
      clerkSessionId: clerkSession?.id,
      datoToken: !!datoCtx?.currentUserAccessToken,
    });

    // A ticket sign-in is still settling — we can't yet know whether the
    // user is signed out, so make no redirect decision this pass. The
    // effect re-runs when the ticket resolves (session change or pending
    // flag flip) and decides from real state.
    if (!hasClerkAuth && !hasDatoAuth && ticketSignInPending) {
      return;
    }

    // Only redirect if we're sure auth has loaded and we have no valid auth
    if (!hasClerkAuth && !hasDatoAuth && isClerkLoaded) {
      // Grace window (PLAT-11031): a user who was already authenticated but
      // whose Clerk session momentarily reads null (Clerk refreshes its
      // session on window focus/visibilitychange — e.g. tearing a tab out into
      // a new window) must not be redirected on that transient blip. Hold the
      // decision for REDIRECT_GRACE_MS, leaving the last authenticated state in
      // place (no flicker); if the session returns, the valid-auth branch below
      // cancels the timer, and if it's still gone when the timer fires
      // `graceExpired` flips and this effect re-runs to redirect. A
      // never-authenticated first load has wasAuthenticatedRef=false and
      // redirects immediately, exactly as before.
      if (wasAuthenticatedRef.current && !graceExpired) {
        if (!graceTimerRef.current) {
          graceTimerRef.current = setTimeout(() => {
            graceTimerRef.current = null;
            setGraceExpired(true);
          }, REDIRECT_GRACE_MS);
        }
        return;
      }

      console.debug('Triggering redirect because:', {
        noClerkAuth: !hasClerkAuth,
        noDatoAuth: !hasDatoAuth,
        isClerkLoaded,
      });
      setAuthState({
        isAuthReady: true,
        needsRedirect: true,
        hasValidAuth: false,
      });
      return;
    }

    // Valid auth. Cancel any pending grace timer and reset the elapsed flag so
    // a later transient drop gets a fresh window.
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
    if (graceExpired) {
      setGraceExpired(false);
    }
    wasAuthenticatedRef.current = true;
    setAuthState({
      isAuthReady: true,
      needsRedirect: false,
      hasValidAuth: hasClerkAuth || hasDatoAuth,
    });
  }, [
    datoCtx,
    clerkUser,
    clerkSession,
    isClerkLoaded,
    clerkTimedOut,
    ticketSignInPending,
    graceExpired,
  ]);

  return authState;
}
